import { useState } from 'react'
import { supabase } from '../lib/supabase'
import { useI18n } from '../i18n/useI18n'
import { useAuth } from '../auth/useAuth'
import { useAsync } from '../lib/useAsync'
import { formatDate } from '../lib/format'
import { canDelete } from '../lib/types'
import { Sheet } from '../components/Sheet'
import { PhotoField } from '../components/PhotoField'
import { DocumentLink } from '../components/DocumentViewer'
import { Loading, ErrorState } from '../components/States'

interface Piece {
  id: string
  kind: string
  label: string | null
  uploaded_at: string
  byte_size: number | null
}

/**
 * Les pieces d'un bail : le contrat signe et tout ce qui
 * l'accompagne.
 *
 * UN BAIL PORTE AUTANT DE PIECES QU'IL EN A. Un contrat fait
 * plusieurs pages : celle des signatures, les conditions, l'etat des
 * lieux d'entree, une piece d'identite. Il fallait auparavant n'en
 * choisir qu'une et les autres restaient dehors.
 *
 * La PIECE PRINCIPALE, leases.contract_document, reste designee : c'est
 * celle qu'on montre en premier, normalement la page des signatures.
 * La premiere piece envoyee le devient automatiquement, et on peut
 * ensuite designer une autre.
 */
export function LeaseContractSheet({
  leaseId,
  unitId,
  tenantId,
  currentDocumentId,
  onClose,
  onSaved,
}: {
  leaseId: string
  unitId: string
  tenantId: string
  /** Piece principale actuelle, s'il y en a une. */
  currentDocumentId: string | null
  onClose: () => void
  onSaved: () => void
}) {
  const { t } = useI18n()
  const { profile } = useAuth()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const peutDetacher = canDelete(profile?.role ?? null)

  const { data, loading, error: loadError, reload } = useAsync(
    async () => {
      const { data: rows, error: e } = await supabase
        .from('documents')
        .select('id, kind, label, uploaded_at, byte_size')
        .eq('lease_id', leaseId)
        .order('uploaded_at', { ascending: true })
      if (e) throw e
      return (rows ?? []) as unknown as Piece[]
    },
    [leaseId],
  )

  const pieces = data ?? []

  /** Rattache la piece envoyee au bail, et la designe si c'est la premiere. */
  async function attacher(documentId: string | null) {
    if (!documentId) return
    setError(null)
    setBusy(true)
    try {
      // La piece principale n'est designee que si aucune ne l'est
      // encore : on ne remplace jamais celle des signatures sans que
      // ce soit demande.
      if (currentDocumentId === null) {
        const { error: e } = await supabase
          .from('leases')
          .update({ contract_document: documentId })
          .eq('id', leaseId)
        if (e) throw new Error(e.message)
      }
      reload()
      onSaved()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  /** Designe cette piece comme la principale. */
  async function designer(documentId: string) {
    setError(null)
    setBusy(true)
    const { error: e } = await supabase
      .from('leases')
      .update({ contract_document: documentId })
      .eq('id', leaseId)
    setBusy(false)
    if (e) {
      setError(e.message)
      return
    }
    onSaved()
  }

  /**
   * Retire la piece de ce bail SANS la supprimer : le fichier reste
   * dans le seau et la piece reste consultable dans l'ecran Pieces.
   * Une photo mal cadree se remplace, elle ne justifie pas de detruire
   * un document.
   */
  async function detacher(documentId: string) {
    setError(null)
    setBusy(true)
    try {
      if (currentDocumentId === documentId) {
        const { error: e1 } = await supabase
          .from('leases')
          .update({ contract_document: null })
          .eq('id', leaseId)
        if (e1) throw new Error(e1.message)
      }
      const { error: e2 } = await supabase
        .from('documents')
        .update({ lease_id: null })
        .eq('id', documentId)
      if (e2) throw new Error(e2.message)
      reload()
      onSaved()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Sheet
      title={t('lease_contract_title')}
      onClose={onClose}
      onSubmit={onClose}
      submitLabel={t('back')}
      submitting={busy}
      error={error}
    >
      <p className="field__hint">{t('lease_contract_hint_multi')}</p>

      {loading && <Loading />}
      {loadError && <ErrorState message={loadError} onRetry={reload} />}

      {!loading && pieces.length === 0 && (
        <div className="banner banner--warn">
          <span>{t('lease_contract_none')}</span>
        </div>
      )}

      {pieces.length > 0 && (
        <>
          <h3 className="screen__section">
            {t('lease_contract_pages', { n: pieces.length })}
          </h3>
          <ul className="minilist">
            {pieces.map((p, i) => (
              <li key={p.id} className="minilist__item minilist__item--stack">
                <span className="list__main">
                  <span className="list__name">
                    {t('lease_contract_page', { n: i + 1 })}
                    {p.id === currentDocumentId && (
                      <span className="chip chip--ok">
                        {t('lease_contract_primary')}
                      </span>
                    )}
                  </span>
                  <span className="list__note">
                    {formatDate(p.uploaded_at.slice(0, 10))}
                    {p.byte_size
                      ? ` · ${Math.max(1, Math.round(p.byte_size / 1024))} Ko`
                      : ''}
                  </span>
                </span>
                <span className="minilist__actions">
                  <DocumentLink documentId={p.id} />
                  {p.id !== currentDocumentId && (
                    <button
                      type="button"
                      className="btn btn--ghost btn--small"
                      disabled={busy}
                      onClick={() => void designer(p.id)}
                    >
                      {t('lease_contract_set_primary')}
                    </button>
                  )}
                  {peutDetacher && (
                    <button
                      type="button"
                      className="btn btn--danger btn--small"
                      disabled={busy}
                      onClick={() => void detacher(p.id)}
                    >
                      {t('lease_contract_detach')}
                    </button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}

      {/* Ajout d'une piece. Le champ se remet a zero apres chaque
          envoi, pour pouvoir enchainer les pages sans refermer. */}
      <h3 className="screen__section">{t('lease_contract_add_page')}</h3>
      <PhotoField
        key={pieces.length}
        label={t('lease_contract_add')}
        target={{ kind: 'contrat', unitId, tenantId, leaseId }}
        documentId={null}
        onUploaded={(id) => void attacher(id)}
      />
    </Sheet>
  )
}
